import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const RgpdPage = () => {
  const { t } = useTranslation();
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          Politique de Protection des Données (RGPD)
        </PageTitle>
        <PageDate
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ delay: 0.1 }}
        >
          {t('legal.lastUpdated')}: 18 Avril 2025
        </PageDate>
      </PageHeader>

      <ContentContainer
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        transition={{ delay: 0.2 }}
      >
        <IntroSection>
          Conformément au Règlement Général sur la Protection des Données (RGPD), cette politique détaille comment nous traitons vos données personnelles.
        </IntroSection>
        
        <Section>
          <SectionTitle>1. Responsable du Traitement</SectionTitle>
          <SectionContent>
            Xenya, situé au 123 Beauty Lane, Paris, France, est le responsable du traitement de vos données personnelles.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>2. Données Collectées</SectionTitle>
          <SectionContent>
            Nous collectons les données suivantes:
            <List>
              <ListItem><Strong>Informations personnelles:</Strong> nom, prénom, adresse email</ListItem>
              <ListItem><Strong>Images faciales:</Strong> pour l'analyse cutanée</ListItem>
              <ListItem><Strong>Données d'utilisation:</Strong> historique d'analyses, préférences</ListItem>
              <ListItem><Strong>Données de paiement:</Strong> pour les abonnements et achats in-app</ListItem>
              <ListItem><Strong>Données techniques:</Strong> identifiant de l'appareil, adresse IP</ListItem>
            </List>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>3. Bases Juridiques du Traitement</SectionTitle>
          <SectionContent>
            Nous traitons vos données sur les bases juridiques suivantes:
            <List>
              <ListItem><Strong>Exécution du contrat:</Strong> fourniture de nos services d'analyse cutanée</ListItem>
              <ListItem><Strong>Consentement:</Strong> pour le traitement de vos images faciales et communications marketing</ListItem>
              <ListItem><Strong>Intérêt légitime:</Strong> amélioration de nos services et sécurité</ListItem>
              <ListItem><Strong>Obligation légale:</Strong> respect des lois applicables</ListItem>
            </List>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>4. Finalités du Traitement</SectionTitle>
          <SectionContent>
            Vos données sont traitées pour:
            <List>
              <ListItem>Fournir nos services d'analyse cutanée personnalisée</ListItem>
              <ListItem>Gérer votre compte et abonnement</ListItem>
              <ListItem>Améliorer nos algorithmes et services</ListItem>
              <ListItem>Vous envoyer des recommandations personnalisées</ListItem>
              <ListItem>Assurer la sécurité de l'application</ListItem>
            </List>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>5. Durée de Conservation</SectionTitle>
          <SectionContent>
            Nous conservons vos données:
            <List>
              <ListItem><Strong>Données de compte:</Strong> pendant la durée de votre utilisation de l'application</ListItem>
              <ListItem><Strong>Images faciales:</Strong> 12 mois après la dernière utilisation</ListItem>
              <ListItem><Strong>Données de paiement:</Strong> selon les obligations légales (5 ans)</ListItem>
            </List>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>6. Vos Droits</SectionTitle>
          <SectionContent>
            Conformément au RGPD, vous disposez des droits suivants:
            <List>
              <ListItem>Droit d'accès à vos données personnelles</ListItem>
              <ListItem>Droit de rectification des données inexactes</ListItem>
              <ListItem>Droit à l'effacement (droit à l'oubli)</ListItem>
              <ListItem>Droit à la limitation du traitement</ListItem>
              <ListItem>Droit à la portabilité de vos données</ListItem>
              <ListItem>Droit d'opposition au traitement</ListItem>
              <ListItem>Droit de retirer votre consentement à tout moment</ListItem>
            </List>
            <RightsExercise>
              Pour exercer ces droits, veuillez nous contacter à <a href="mailto:dpo@xenya-face.app">dpo@xenya-face.app</a> ou via le formulaire de contact disponible dans la section Support de notre application.
            </RightsExercise>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>7. Destinataires des Données</SectionTitle>
          <SectionContent>
            Vos données peuvent être partagées avec:
            <List>
              <ListItem>Nos sous-traitants techniques (hébergement, paiement)</ListItem>
              <ListItem>Partenaires commerciaux (avec votre consentement)</ListItem>
              <ListItem>Autorités légales en cas d'obligation légale</ListItem>
            </List>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>8. Transferts Internationaux</SectionTitle>
          <SectionContent>
            Certaines de vos données peuvent être transférées hors de l'Union Européenne. Dans ce cas, nous garantissons un niveau de protection adéquat conformément au RGPD, notamment par:
            <List>
              <ListItem>Le recours à des clauses contractuelles types approuvées par la Commission Européenne</ListItem>
              <ListItem>Le transfert vers des pays reconnus comme offrant une protection adéquate</ListItem>
              <ListItem>La mise en place de garanties appropriées et de mesures supplémentaires de sécurité</ListItem>
            </List>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>9. Mesures de Sécurité</SectionTitle>
          <SectionContent>
            Pour protéger vos données personnelles, nous avons mis en place des mesures de sécurité techniques et organisationnelles appropriées, notamment:
            <List>
              <ListItem>Chiffrement des données sensibles</ListItem>
              <ListItem>Accès limité aux données personnelles</ListItem>
              <ListItem>Audits de sécurité réguliers</ListItem>
              <ListItem>Formation du personnel aux bonnes pratiques en matière de protection des données</ListItem>
              <ListItem>Mise à jour régulière de nos systèmes de sécurité</ListItem>
            </List>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>10. Utilisation des Cookies</SectionTitle>
          <SectionContent>
            Notre application utilise des cookies et technologies similaires pour améliorer votre expérience. Ces technologies nous permettent de:
            <List>
              <ListItem>Mémoriser vos préférences et paramètres</ListItem>
              <ListItem>Comprendre comment vous utilisez notre application</ListItem>
              <ListItem>Améliorer nos services et fonctionnalités</ListItem>
              <ListItem>Vous offrir une expérience personnalisée</ListItem>
            </List>
            Vous pouvez gérer vos préférences en matière de cookies dans les paramètres de l'application.
          </SectionContent>
        </Section>

        <ContactSection>
          <SectionTitle>11. Contact</SectionTitle>
          <SectionContent>
            Pour exercer vos droits ou pour toute question, contactez notre Délégué à la Protection des Données:
            <ContactInfo>
              <div>Email: dpo@xenya-face.app</div>
              <div>Adresse: 123 Beauty Lane, Paris, France</div>
              <div>Téléphone: +33 1 23 45 67 89</div>
            </ContactInfo>
            <ReclamationCnil>
              Vous avez également le droit d'introduire une réclamation auprès de la CNIL (www.cnil.fr) si vous estimez que le traitement de vos données constitue une violation du RGPD.
            </ReclamationCnil>
          </SectionContent>
        </ContactSection>
      </ContentContainer>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  padding-top: 80px;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: ${({ theme }) => theme.colors.gradientPrimary};
`;

const PageTitle = styled(motion.h1)`
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: 10px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
  }
`;

const PageDate = styled(motion.p)`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.gray};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

const ContentContainer = styled(motion.div)`
  max-width: 900px;
  margin: 0 auto;
  padding: 60px 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  backdrop-filter: blur(10px);
  margin-top: -30px;
  margin-bottom: 60px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const IntroSection = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: 1.7;
  margin-bottom: 40px;
  color: ${({ theme }) => theme.colors.light};
  opacity: 0.9;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.md};
  }
`;

const Section = styled.div`
  margin-bottom: 40px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-bottom: 15px;
  color: ${({ theme }) => theme.colors.accent};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
`;

const SectionContent = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.md};
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.light};
  opacity: 0.9;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

const List = styled.ul`
  padding-left: 20px;
  margin: 15px 0;
`;

const ListItem = styled.li`
  margin-bottom: 8px;
  position: relative;
  padding-left: 5px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:before {
    content: '•';
    position: absolute;
    left: -15px;
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const Strong = styled.span`
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
`;

const RightsExercise = styled.p`
  margin-top: 15px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  
  a {
    color: ${({ theme }) => theme.colors.accent};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ContactSection = styled(Section)`
  margin-top: 60px;
  padding-top: 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ContactInfo = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-top: 15px;
  line-height: 1.8;
`;

const ReclamationCnil = styled.p`
  margin-top: 20px;
  font-style: italic;
`;

export default RgpdPage;